import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@repo/ui';
import { trustRequestApi } from '../api';
import type {
  TrustEligibleParams,
  BulkTrustRequestDto,
  IndividualTrustRequestDto,
  ClientFilterParams,
  MatterFilterParams,
} from '../types';

// Query keys
export const trustRequestKeys = {
  all: ['trust-requests'] as const,
  clients: (params?: ClientFilterParams) => [...trustRequestKeys.all, 'clients', params] as const,
  mattersForFiltering: (params?: MatterFilterParams) => [...trustRequestKeys.all, 'matters-for-filtering', params] as const,
  trustEligible: (params: TrustEligibleParams) => [...trustRequestKeys.all, 'trust-eligible', params] as const,
  trustCalculation: (matterId: string) => [...trustRequestKeys.all, 'trust-calculation', matterId] as const,
};

/**
 * Query hook for fetching clients for filtering dropdown
 */
export function useClientsForFiltering(params?: ClientFilterParams) {
  return useQuery({
    queryKey: trustRequestKeys.clients(params),
    queryFn: () => trustRequestApi.getClientsForFiltering(params),
    staleTime: 15 * 60 * 1000, // 15 minutes (clients change infrequently)
  });
}

/**
 * Query hook for fetching ALL matters for filtering dropdown
 * Unlike useTrustEligibleMatters, this returns all matters regardless of trust status
 */
export function useMattersForFiltering(params?: MatterFilterParams) {
  return useQuery({
    queryKey: trustRequestKeys.mattersForFiltering(params),
    queryFn: () => trustRequestApi.getMattersForFiltering(params),
    staleTime: 15 * 60 * 1000, // 15 minutes (matters change infrequently)
  });
}

/**
 * Query hook for fetching matters that need trust requests
 */
export function useTrustEligibleMatters(params: TrustEligibleParams) {
  return useQuery({
    queryKey: trustRequestKeys.trustEligible(params),
    queryFn: () => trustRequestApi.getTrustEligibleMatters(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Query hook for fetching trust calculation breakdown for a specific matter
 */
export function useTrustCalculation(matterId: string) {
  return useQuery({
    queryKey: trustRequestKeys.trustCalculation(matterId),
    queryFn: () => trustRequestApi.getTrustCalculation(matterId),
    enabled: !!matterId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Mutation for creating bulk trust requests
 */
export function useCreateBulkTrustRequests() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkTrustRequestDto) => trustRequestApi.createBulkTrustRequests(data),
    onSuccess: (data) => {
      showToast.success(`Queued ${data.queuedCount} trust requests for processing`);
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: trustRequestKeys.trustEligible });
    },
    onError: (error: Error) => {
      showToast.error(`Failed to create trust requests: ${error.message}`);
    },
  });
}

/**
 * Mutation for creating individual trust request
 */
export function useCreateIndividualTrustRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: IndividualTrustRequestDto) => trustRequestApi.createIndividualTrustRequest(data),
    onSuccess: () => {
      showToast.success('Trust request queued for processing');
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: trustRequestKeys.trustCalculation });
    },
    onError: (error: Error) => {
      showToast.error(`Failed to create trust request: ${error.message}`);
    },
  });
}


